/* eslint-disable react/prop-types */
import React from "react";
import { Bar } from "react-chartjs-2";
import YearPicker from "../common/yearpicker";
import CommonDashDropdown from "../common/common_dropDownDashboard";
import numberFormat from "../common/number-format";
import "./SpendAnalyticsRow.css";

export const SpendAnalyticsRow = ({
  apiUrl,
  productOption,
  setProductOption,
  projectOption,
  setProjectOption,
  selectedProductYear,
  setSelectedProductYear,
  productStValDetails = {},
  productStDetails = {},
  selectedVendorYear,
  setSelectedVendorYear,
  vendorOption,
  setVendorOption,
  vendorStDetails = {},
  vendorStVaDetails = {},
  activeMonthIndex,
  setActiveMonthIndex,
}) => {
  const topColors = (Array.isArray(vendorStDetails?.labels) ? vendorStDetails.labels : []).map((_, i) => {
    return i === activeMonthIndex ? "#3B5BDB" : "rgba(59,91,219,0.15)";
  });

  const bottomColors = (Array.isArray(vendorStDetails?.labels) ? vendorStDetails.labels : []).map((_, i) => {
    return i === activeMonthIndex ? "#3B5BDB" : "rgba(59,91,219,0.15)";
  });

  const dataForBar = {
    labels: Array.isArray(productStDetails?.period) ? productStDetails.period : [],
    datasets: [
      {
        label: "Spent",
        data: Array.isArray(productStDetails?.amount) ? productStDetails.amount : [],
        backgroundColor: "#3B5BDB", // Blue
        borderRadius: 8,
        barThickness: 8,
      },
      {
        label: "Products",
        data: Array.isArray(productStDetails?.product) ? productStDetails.product : [],
        backgroundColor: "#FD7E14", // Orange
        borderRadius: 8,
        barThickness: 8,
        minBarLength: 8,
      },
    ],
  };

  const dataForVendor = {
    labels: Array.isArray(vendorStDetails?.labels) ? vendorStDetails.labels : [],
    datasets: [
      {
        label: "Top",
        data: Array.isArray(vendorStDetails?.data) ? vendorStDetails.data.map((v) => v) : [],
        backgroundColor: topColors,
        borderRadius: 20,
        barThickness: 18,
        stack: "combined",
      },
      {
        label: "Bottom",
        data: Array.isArray(vendorStDetails?.data) ? vendorStDetails.data.map((v) => -v) : [],
        backgroundColor: bottomColors,
        borderRadius: 20,
        barThickness: 18,
        stack: "combined",
      },
    ],
  };

  const optionsForBar = {
    responsive: true,
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: false, // disable default tooltip
        mode: "index",
        intersect: false,
        external: function (context) {
          let tooltipEl = document.getElementById("chartjs-tooltip");

          if (!tooltipEl) {
            tooltipEl = document.createElement("div");
            tooltipEl.id = "chartjs-tooltip";
            tooltipEl.style.position = "absolute";
            tooltipEl.style.background = "#fff";
            tooltipEl.style.border = "1px solid #E0E0E0";
            tooltipEl.style.borderRadius = "8px";
            tooltipEl.style.boxShadow = "0px 2px 6px rgba(0,0,0,0.1)";
            tooltipEl.style.padding = "8px 12px";
            tooltipEl.style.pointerEvents = "none";
            tooltipEl.style.transition = "all .1s ease";
            document.body.appendChild(tooltipEl);
          }

          const tooltipModel = context.tooltip;

          if (tooltipModel.opacity === 0) {
            tooltipEl.style.opacity = 0;
            return;
          }

          if (tooltipModel.body) {
            const title = tooltipModel.title[0];
            const bodyLines = tooltipModel.dataPoints;

            let innerHtml = `
              <div style="font-weight:600; color:#3B5BDB; margin-bottom:6px;">
                ${title} 2025
              </div>
            `;

            bodyLines.forEach((bodyItem) => {
              const dataset = bodyItem.dataset;
              const color = dataset.backgroundColor;
              const label = dataset.label;
              const value = bodyItem.raw;

              innerHtml += `
                <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:4px;">
                  <div style="display:flex; align-items:center; gap:6px;">
                    <span style="display:inline-block; width:8px; height:8px; border-radius:50%; background:${color};"></span>
                    <span style="font-size:13px; color:#555;">${label}</span>
                  </div>
                  <div style="font-weight:600; color:#000;">
                    ${label === "Spent" ? "₹" : ""}
                    ${numberFormat(value, 2)}
                  </div>
                </div>
              `;
            });

            tooltipEl.innerHTML = innerHtml;
          }

          const { offsetLeft: positionX, offsetTop: positionY } =
            context.chart.canvas;
          tooltipEl.style.opacity = 1;
          tooltipEl.style.left = positionX + tooltipModel.caretX + "px";
          tooltipEl.style.top = positionY + tooltipModel.caretY + "px";
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: true,
          drawTicks: false,
          drawBorder: false,
          color: "#E0E0E0",
          borderDash: [4, 4],
        },
        ticks: {
          maxTicksLimit: 5,
          color: "#7D7D7D",
          font: { size: 12 },
          borderDash: [4, 4],
        },
      },
      y: {
        beginAtZero: true,
        ticks: { maxTicksLimit: 5, borderDash: [4, 4] },
        grid: {
          color: "#E0E0E0",
        },
      },
    },
  };

  const optionsForVendor = {
    responsive: true,
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false },
    },
    onClick: (evt, elements) => {
      if (elements.length > 0) {
        const index = elements[0].index;
        setActiveMonthIndex(index);
      }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          color: "#7D7D7D",
          font: { size: 12 },
        },
        stacked: true,
      },
      y: {
        stacked: true,
        grid: { display: false },
        ticks: { display: false },
        grace: "1%",
      },
    },
  };

  return (
    <div className="row mt-3">
      {/* Product Based Spending */}
      <div className="col-xl-8">
        <div className="bg-white rounded-4 p-3">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="fw-bold">Product based spending</h6>
            <div className="d-flex gap-2">
              <div className="mt-1">
                <CommonDashDropdown
                  apiUrl={`${apiUrl}get_product`}
                  placeholder="Select"
                  value={productOption}
                  onSelect={(item) => {
                    setProductOption(item);
                  }}
                />
              </div>
              <div className="mt-1">
                <CommonDashDropdown
                  apiUrl={`${apiUrl}get_project`}
                  placeholder="Select"
                  value={projectOption}
                  searchView={true}
                  onSelect={(item) => {
                    setProjectOption(item);
                  }}
                />
              </div>
              <div>
                <YearPicker
                  selectedYear={selectedProductYear}
                  setSelectedYear={setSelectedProductYear}
                />
              </div>
            </div>
          </div>

          <div className="d-flex align-items-center gap-3 mb-2">
            <h5 className="fw-bold mb-0">
              ₹ {productStValDetails.total ?? "0"}
            </h5>
            <span className="badge bg-success-subtle text-success">
              {productStValDetails.decreased === true ? " ↓" : "↑"}{" "}
              {productStValDetails.diff ?? "0"}%
            </span>
          </div>

          <Bar data={dataForBar} options={optionsForBar} />
        </div>
      </div>

      {/* Vendor Based Spending */}
      <div className="col-xl-4">
        <div className="bg-white h-100 rounded-4 p-3">
          <div className="card h-100 border-0">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="fw-semibold mb-0">Vendor based spending</h6>
              <div>
                <YearPicker
                  selectedYear={selectedVendorYear}
                  setSelectedYear={setSelectedVendorYear}
                />
              </div>
            </div>

            <CommonDashDropdown
              apiUrl={`${apiUrl}get_vendor`}
              placeholder="Select"
              value={vendorOption}
              onSelect={(item) => {
                setVendorOption(item);
              }}
            />

            <div className="mt-2 mb-3">
              <Bar data={dataForVendor} options={optionsForVendor} />
            </div>

            <div className="d-flex align-items-center gap-2 mb-1">
              <h5 className="fw-bold mb-0">
                ₹{" "}
                {numberFormat(
                  vendorStDetails?.data?.[activeMonthIndex] ?? 0,
                )}
              </h5>
            </div>

            <div className="d-flex justify-content-between align-items-center mb-3">
              <div className="d-flex align-items-center gap-1">
                <span className="text-success fw-semibold">
                  {vendorStVaDetails.decreased === true ? " ↓" : "↑"}{" "}
                  {vendorStVaDetails.diff ?? "0"}%
                </span>
                <small className="text-muted">
                  {vendorStVaDetails.decreased === true
                    ? "decreased from purchase"
                    : "   increased from purchase."}
                </small>
              </div>
              <small className="text-muted">
                {vendorStVaDetails.last_year ?? ""}
              </small>
            </div>

            <div className="border-top pt-2 d-flex justify-content-between align-items-center">
              <small className="text-muted fw-medium">
                Total spent amount
              </small>
              <small className="fw-bold text-primary">
                ₹ {vendorStVaDetails.total ?? ""}
              </small>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
